// Peças visuais de insights reaproveitadas no início, campanhas, detalhe e resultados.
import { isValidElement, useEffect, useState, type ComponentType, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { ArrowRight, CircleDollarSign, Plug, RefreshCw } from 'lucide-react'
import { brl, num } from '@impulso/shared'
import type { DailyPointDto, PacingDto } from '@impulso/shared/api/insights'
import type { DailyPoint } from '../../ui/charts'
import './insights.css'

/** Pontos do gráfico investimento × clientes. */
export function toChartData(daily: DailyPointDto[]): DailyPoint[] {
  return daily.map((d) => ({
    date: `${d.date}T00:00:00Z`,
    label: `${d.date.slice(8, 10)}/${d.date.slice(5, 7)}`,
    spend: d.spend,
    contacts: d.results,
  }))
}

/** Dinheiro: centavos só quando importam. */
export function money(v: number | null | undefined): string {
  if (v == null) return '—'
  const r = Math.round(v * 100) / 100
  return brl(r, Number.isInteger(r) || Math.abs(r) >= 1000 ? 0 : 2)
}

export const money2 = (v: number | null | undefined) => (v == null ? '—' : brl(v, 2))

export function roasText(roas: number | null): string {
  return roas == null ? '—' : `${num(roas, 1)}x`
}

/** Rótulo do período de comparação das variações (o servidor compara com o período anterior de mesmo tamanho). */
export const vsLabel = (days: number) => `vs. ${days} dias anteriores`

export function Kpi({ icon: Icon, label, value, delta, hint, vs, small }: { icon?: ComponentType<{ size?: number }>; label: string; value: ReactNode; delta?: ReactNode; hint?: ReactNode; vs?: string; small?: boolean }) {
  // <Delta> devolve null sem valor: só mostra o "vs." quando há variação de fato
  const hasDelta = isValidElement(delta) && Number.isFinite((delta.props as { value?: number | null }).value)
  return (
    <div className={`card kpi${small ? ' sm' : ''}`}>
      <div className="label">{Icon && <Icon size={15} />} {label}</div>
      <div className="kpi-top">
        <div className="value">{value}</div>
        {hasDelta && <>{delta}{vs && <span className="kpi-vs">{vs}</span>}</>}
      </div>
      {hint && <div className="hint">{hint}</div>}
    </div>
  )
}

export function PacingCard({ pacing }: { pacing: PacingDto }) {
  const used = pacing.budget ? Math.min(1, pacing.spent / pacing.budget) : 0
  const expected = pacing.budget && pacing.expectedSpent != null ? Math.min(1, pacing.expectedSpent / pacing.budget) : null
  const color = pacing.status === 'over' ? 'var(--amber)' : 'var(--primary)'
  return (
    <div className="card">
      <div className="card-head">
        <span className="ic purple"><CircleDollarSign size={18} /></span>
        <div>
          <h3>Orçamento de {pacing.month}</h3>
          <div className="small muted">Dia {pacing.day} de {pacing.daysInMonth}</div>
        </div>
      </div>
      <div className="row between">
        <span className="strong" style={{ fontSize: 20 }}>{money(pacing.spent)}</span>
        {pacing.budget != null && <span className="muted">de {money(pacing.budget)}</span>}
      </div>
      {pacing.budget != null && (
        <div className="progress mt-8" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(used * 100)} aria-label="Quanto do orçamento do mês já foi usado">
          <span style={{ width: `${used * 100}%`, background: color }} />
          {expected != null && <i className="marker" style={{ left: `${expected * 100}%` }} title="Onde deveria estar hoje" />}
        </div>
      )}
      <p className="small muted mt-8">{pacing.message}</p>
    </div>
  )
}

export function SyncingBanner() {
  return (
    <div className="callout" role="status">
      <RefreshCw size={18} className="spin" color="var(--primary)" style={{ flexShrink: 0, marginTop: 2 }} />
      <span><b>Estamos trazendo seus dados.</b> A primeira sincronização pode levar alguns minutos. Esta tela atualiza sozinha.</span>
    </div>
  )
}

export function NoConnections({ companyId, canConnect }: { companyId: string; canConnect: boolean }) {
  return (
    <div className="card empty">
      <span className="ic purple" style={{ margin: '0 auto 12px' }}><Plug size={18} /></span>
      <h3 style={{ color: 'var(--text)' }}>Conecte suas contas de anúncio</h3>
      <p className="mt-8">Ligue sua conta do Google, do Instagram/Facebook e do TikTok para ver aqui quanto você investe e quantos clientes chegam.</p>
      {canConnect
        ? <Link to={`/app/${companyId}/contas`} className="btn primary mt-16">Conectar contas <ArrowRight size={16} /></Link>
        : <p className="small mt-12">Peça para quem administra a empresa conectar as contas.</p>}
    </div>
  )
}

export function lastSyncText(iso: string | null): string {
  if (!iso) return 'Ainda não sincronizado'
  const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60_000)
  if (mins < 1) return 'Atualizado agora'
  if (mins < 60) return `Atualizado há ${mins} ${mins === 1 ? 'minuto' : 'minutos'}`
  const h = Math.round(mins / 60)
  if (h < 24) return `Atualizado há ${h} ${h === 1 ? 'hora' : 'horas'}`
  return `Atualizado em ${new Date(iso).toLocaleDateString('pt-BR')}`
}

/** true em telas estreitas (celular): troca tabelas por listas. */
export function useIsNarrow(maxWidth = 640): boolean {
  const query = `(max-width: ${maxWidth}px)`
  const [narrow, setNarrow] = useState(() => typeof window !== 'undefined' && window.matchMedia(query).matches)
  useEffect(() => {
    const m = window.matchMedia(query)
    const on = () => setNarrow(m.matches)
    m.addEventListener('change', on)
    return () => m.removeEventListener('change', on)
  }, [query])
  return narrow
}

export function greeting(date = new Date()): string {
  const h = date.getHours()
  if (h < 12) return 'Bom dia'
  if (h < 18) return 'Boa tarde'
  return 'Boa noite'
}
